import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Save, Plus, Trash2 } from "lucide-react";

/* ─── Global Settings Tab ─── */
function SeoGlobalTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-seo-global"],
    queryFn: async () => {
      const { data, error } = await supabase.from("seo_global_settings").select("*").limit(1).single();
      if (error) throw error;
      return data;
    },
  });

  const [form, setForm] = useState<Record<string, any>>({});
  const get = (key: string) => (key in form ? form[key] : (data as any)?.[key] ?? "");
  const set = (key: string, val: any) => setForm((p) => ({ ...p, [key]: val }));

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("seo_global_settings")
        .update({
          site_name: get("site_name"),
          title_suffix: get("title_suffix"),
          default_meta_description: get("default_meta_description"),
          default_indexable: get("default_indexable"),
          default_robots: get("default_robots"),
        } as any)
        .eq("id", data!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-seo-global"] });
      setForm({});
      toast({ title: "SEO global actualizado" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Configuración global</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4 max-w-lg">
          <div className="space-y-2">
            <Label>Nombre del sitio</Label>
            <Input value={get("site_name")} onChange={(e) => set("site_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Sufijo del título</Label>
            <Input value={get("title_suffix")} onChange={(e) => set("title_suffix", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Meta description por defecto</Label>
            <Textarea value={get("default_meta_description")} onChange={(e) => set("default_meta_description", e.target.value)} rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Robots por defecto</Label>
            <Input value={get("default_robots")} onChange={(e) => set("default_robots", e.target.value)} />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={get("default_indexable") ?? true} onCheckedChange={(v) => set("default_indexable", v)} />
            <Label>Indexable por defecto</Label>
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {mutation.isPending ? "Guardando..." : "Guardar"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/* ─── Templates Tab ─── */
function SeoTemplatesTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ["admin-seo-templates"],
    queryFn: async () => {
      const { data, error } = await supabase.from("seo_templates").select("*").order("page_type");
      if (error) throw error;
      return data;
    },
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});

  const startEdit = (t: any) => {
    setEditing(t.id);
    setForm({ title_template: t.title_template, meta_description_template: t.meta_description_template, robots_directive: t.robots_directive ?? "", indexable: t.indexable, canonical_template: t.canonical_template ?? "" });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("seo_templates").update(form as any).eq("id", editing!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-seo-templates"] });
      setEditing(null);
      toast({ title: "Plantilla actualizada" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Variables disponibles: {"{{nombre_sala}}, {{ciudad}}, {{comunidad}}, {{tipo}}"}
      </p>
      {templates?.map((t) => (
        <Card key={t.id}>
          <CardContent className="p-4">
            {editing === t.id ? (
              <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }} className="space-y-3">
                <p className="text-sm font-semibold">{t.page_type}</p>
                <div className="space-y-1">
                  <Label className="text-xs">Title template</Label>
                  <Input value={form.title_template} onChange={(e) => setForm((p) => ({ ...p, title_template: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Meta description template</Label>
                  <Textarea value={form.meta_description_template} onChange={(e) => setForm((p) => ({ ...p, meta_description_template: e.target.value }))} rows={2} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Robots directive</Label>
                  <Input value={form.robots_directive} onChange={(e) => setForm((p) => ({ ...p, robots_directive: e.target.value }))} placeholder="index,follow" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Canonical template</Label>
                  <Input value={form.canonical_template} onChange={(e) => setForm((p) => ({ ...p, canonical_template: e.target.value }))} placeholder="/sala/{{slug}}" />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={saveMutation.isPending}>Guardar</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                </div>
              </form>
            ) : (
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold">{t.page_type}</p>
                  <p className="text-xs text-muted-foreground mt-1">Title: {t.title_template}</p>
                  <p className="text-xs text-muted-foreground">Desc: {t.meta_description_template}</p>
                  {t.robots_directive && <p className="text-xs text-muted-foreground">Robots: {t.robots_directive}</p>}
                  {t.canonical_template && <p className="text-xs text-muted-foreground">Canonical: {t.canonical_template}</p>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => startEdit(t)}>Editar</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ─── Overrides Tab ─── */
function SeoOverridesTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: overrides, isLoading } = useQuery({
    queryKey: ["admin-seo-overrides"],
    queryFn: async () => {
      const { data, error } = await supabase.from("seo_overrides").select("*").order("page_type");
      if (error) throw error;
      return data;
    },
  });

  const [newOverride, setNewOverride] = useState({ page_type: "", entity_id: "", custom_title: "", custom_meta_description: "", custom_robots_directive: "", custom_canonical: "" });

  const addMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("seo_overrides").insert({
        page_type: newOverride.page_type,
        entity_id: newOverride.entity_id || null,
        custom_title: newOverride.custom_title || null,
        custom_meta_description: newOverride.custom_meta_description || null,
        custom_robots_directive: newOverride.custom_robots_directive || null,
        custom_canonical: newOverride.custom_canonical || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-seo-overrides"] });
      setNewOverride({ page_type: "", entity_id: "", custom_title: "", custom_meta_description: "", custom_robots_directive: "", custom_canonical: "" });
      toast({ title: "Override añadido" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("seo_overrides").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-seo-overrides"] });
      toast({ title: "Override eliminado" });
    },
  });

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-6">
      {/* Add new */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Nuevo override</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); addMutation.mutate(); }} className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Page type *</Label>
              <Input value={newOverride.page_type} onChange={(e) => setNewOverride((p) => ({ ...p, page_type: e.target.value }))} required placeholder="sala_detail" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Entity ID</Label>
              <Input value={newOverride.entity_id} onChange={(e) => setNewOverride((p) => ({ ...p, entity_id: e.target.value }))} placeholder="slug o ID" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Custom title</Label>
              <Input value={newOverride.custom_title} onChange={(e) => setNewOverride((p) => ({ ...p, custom_title: e.target.value }))} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Custom meta description</Label>
              <Textarea value={newOverride.custom_meta_description} onChange={(e) => setNewOverride((p) => ({ ...p, custom_meta_description: e.target.value }))} rows={2} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Custom robots directive</Label>
              <Input value={newOverride.custom_robots_directive} onChange={(e) => setNewOverride((p) => ({ ...p, custom_robots_directive: e.target.value }))} placeholder="noindex,follow" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Custom canonical</Label>
              <Input value={newOverride.custom_canonical} onChange={(e) => setNewOverride((p) => ({ ...p, custom_canonical: e.target.value }))} placeholder="https://bandflow.es/..." />
            </div>
            <Button type="submit" size="sm" disabled={addMutation.isPending}>
              <Plus className="mr-1 h-4 w-4" /> Añadir
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      {overrides?.map((o) => (
        <Card key={o.id}>
          <CardContent className="flex items-start justify-between p-4">
            <div>
              <p className="text-sm font-semibold">{o.page_type} {o.entity_id ? `→ ${o.entity_id}` : ""}</p>
              {o.custom_title && <p className="text-xs text-muted-foreground">Title: {o.custom_title}</p>}
              {o.custom_meta_description && <p className="text-xs text-muted-foreground">Desc: {o.custom_meta_description}</p>}
              {o.custom_robots_directive && <p className="text-xs text-muted-foreground">Robots: {o.custom_robots_directive}</p>}
              {o.custom_canonical && <p className="text-xs text-muted-foreground">Canonical: {o.custom_canonical}</p>}
            </div>
            <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(o.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </CardContent>
        </Card>
      ))}
      {!overrides?.length && <p className="text-sm text-muted-foreground">No hay overrides configurados.</p>}
    </div>
  );
}

/* ─── Main Page ─── */
export default function AdminSeo() {
  return (
    <AdminLayout requiredModule="seo" requiredPermission="can_view">
      <div>
        <h1 className="mb-6 font-display text-2xl font-bold">Gestión SEO</h1>
        <Tabs defaultValue="global">
          <TabsList>
            <TabsTrigger value="global">Global</TabsTrigger>
            <TabsTrigger value="templates">Plantillas</TabsTrigger>
            <TabsTrigger value="overrides">Overrides</TabsTrigger>
          </TabsList>
          <TabsContent value="global" className="mt-4"><SeoGlobalTab /></TabsContent>
          <TabsContent value="templates" className="mt-4"><SeoTemplatesTab /></TabsContent>
          <TabsContent value="overrides" className="mt-4"><SeoOverridesTab /></TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
